import {
  ArrowDownOnSquareIcon,
  ArrowsPointingOutIcon,
  CheckCircleIcon,
  Square2StackIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { cleanPhone } from "../../functions/cleanPhone";
import DynamicList from "../DynamicList";
import { handleMessageChange } from "../handleMessageChange";
import { Badge } from "../tailwind/badge";
import { Button } from "../tailwind/button";
import { Divider } from "../tailwind/divider";
import { ErrorMessage, Field } from "../tailwind/fieldset";
import { Heading } from "../tailwind/heading";
import { Input } from "../tailwind/input";
import { Link } from "../tailwind/link";
import SelectInput from "../tailwind/select-input";
import { Text } from "../tailwind/text";
import { Textarea } from "../tailwind/textarea";
import SidePanel from "./SidePanel";

const WebhookNodePanel = ({
  headers,
  setHeaders,
  queryParams,
  setQueryParams,
  pathParams,
  setPathParams,
  configResponse,
  setConfigResponse,
  jsonError,
  setJsonError,
  testRequestLoading,
  setTestRequestLoading,
  isSidePanelVariableOpen,
  setIsSidePanelVariableOpen,
  isSidePanelTestOpen,
  setIsSidePanelTestOpen,
  lastContact,
  setLastContact,
  exampleContactPhone,
  setExampleContactPhone,
  exampleContactError,
  setExampleContactError,
  exampleContactLoading,
  setExampleContactLoading,
  responseTestRequest,
  setResponseTestRequest,
  variableCopied,
  setVariableCopied,
  textareaJsonBodyRef,
  nodes,
  setNodes,
  user,
  selectedNode,
  api,
  validateJson,
}) => {
  const methodOptions = [
    { label: "POST", value: "POST" },
    { label: "GET", value: "GET" },
  ];

  const handleOpenSidePanelTest = async () => {
    console.log("search contact");
    if (isSidePanelVariableOpen) {
      setIsSidePanelVariableOpen(false);
    }
    const lastUpdatedContact = await api.contact.maybeFindFirst({
      filter: {
        customerId: { equals: user.customer.id },
      },
      select: {
        id: true,
        firstName: true,
        lastName: true,
        hasOptIn: true,
        userPhoneNumber: true,
        updatedAt: true,
        customProperties: true,
      },
      sort: {
        updatedAt: "Descending",
      },
    });
    setLastContact(lastUpdatedContact);
    setExampleContactPhone(lastUpdatedContact.userPhoneNumber);
    setIsSidePanelTestOpen(true);
  };

  const nativeVariables = [
    "firstName",
    "hasOptIn",
    "userPhoneNumber",
    "lastName",
  ];

  const nativeFieldsOptions = [
    { label: "Firstname", value: "firstName" },
    { label: "Lastname", value: "lastName" },
    { label: "Has optIn", value: "hasOptin" },
  ];

  let customVariables = [];
  let fieldsOptions = [];
  let customFieldsOptions = [];

  if (user?.customer?.customFields) {
    customVariables = Object.keys(user?.customer?.customFields);
    customFieldsOptions = customVariables
      .filter((i) => !!i)
      .map((field) => ({
        label: field,
        value: field,
      }));

    if (selectedNode?.data.method === "POST") {
      fieldsOptions = [...customFieldsOptions];
    } else {
      fieldsOptions = [
        ...nativeFieldsOptions,
        ...customFieldsOptions.filter(
          (customField) =>
            !nativeFieldsOptions.some(
              (nativeField) => nativeField.value === customField.value,
            ),
        ),
      ];
    }
  } else {
    fieldsOptions = [...nativeFieldsOptions];
  }
  console.log(fieldsOptions);

  const handleVariableCopy = (variable) => {
    setVariableCopied(variable);
    if (selectedNode?.data?.method === "POST") {
      navigator.clipboard.writeText(`"{{contact.${variable}}}"`);
    } else if (selectedNode?.data?.method === "GET") {
      navigator.clipboard.writeText(`{{contact.${variable}}}`);
    }
    setTimeout(() => setVariableCopied(null), 1000);
  };

  const handleVariableInsert = (variable, isInTextarea) => {
    if (isInTextarea) {
      const textarea = textareaJsonBodyRef.current;
      const textBefore = textarea.value.substring(0, textarea.selectionStart);
      const textAfter = textarea.value.substring(textarea.selectionEnd);
      const insertionText = `"{{contact.${variable}}}"`;

      const updatedValue = `${textBefore}${insertionText}${textAfter}`;
      textarea.value = updatedValue;

      handleMessageChange("body", updatedValue, nodes, setNodes);

      const newCursorPosition = textBefore.length + insertionText.length;
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);

      validateJson(updatedValue);
    } else {
      const input = window.activeInput;
      const textBefore = input.value.substring(0, input.selectionStart);
      const textAfter = input.value.substring(input.selectionEnd);
      const insertionText = `{{contact.${variable}}}`;

      const updatedValue = `${textBefore}${insertionText}${textAfter}`;
      input.value = updatedValue;

      const stateKey = input.name.split("-value-")[0];
      const index = input.name.split("-value-").pop();
      console.log(stateKey);
      console.log(index);

      const params =
        selectedNode && selectedNode.data ? selectedNode.data[stateKey] : null;
      console.log(params);

      params[index].value = updatedValue;

      handleMessageChange(stateKey, params, nodes, setNodes);

      const newCursorPosition = textBefore.length + insertionText.length;
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);
    }
  };

  const handleSendTestRequest = async () => {
    setTestRequestLoading(true);
    const response = await api.executeWebhookNodeRequest({
      method: selectedNode.data.method,
      url: selectedNode.data.destinationUrl,
      headers:
        selectedNode.data.headers.length === 1 &&
        selectedNode.data.headers[0].key === ""
          ? null
          : JSON.stringify(selectedNode.data.headers, null, 2),
      queryParams:
        selectedNode.data.queryParams.length === 1 &&
        selectedNode.data.queryParams[0].key === ""
          ? null
          : JSON.stringify(selectedNode.data.queryParams, null, 2),
      pathParams:
        selectedNode.data.pathParams.length === 1 &&
        selectedNode.data.pathParams[0].key === ""
          ? null
          : JSON.stringify(selectedNode.data.pathParams, null, 2),
      configResponse:
        selectedNode.data.configResponse.length === 1 &&
        selectedNode.data.configResponse[0].key === ""
          ? null
          : JSON.stringify(selectedNode.data.configResponse, null, 2),
      body: selectedNode.data.body,
      contactNumber: lastContact.userPhoneNumber,
      customerId: user.customer.id,
      isTest: true,
    });

    // Affichez la réponse de la requête
    console.log("Response:", response);
    setResponseTestRequest(response);
    setTestRequestLoading(false);
    return response;
  };

  const formatExampleBody = (body) => {
    console.log(lastContact);
    return body.replace(/{{\s*contact\.([\w]+)\s*}}/g, (_, variable) => {
      console.log(variable);
      // Si la variable fait partie des variables natives
      if (nativeVariables.includes(variable)) {
        return lastContact[variable] || "";
      }
      // Sinon, chercher dans les propriétés personnalisées
      return lastContact.customProperties?.[variable] || "";
    });
  };

  const buildUrl = (baseUrl, pathParams, queryParams) => {
    console.log("Base URL:", baseUrl);
    console.log("Path Params:", pathParams);

    let url = baseUrl;

    pathParams.forEach(({ key, value }) => {
      console.log(`Replacing {${key}} with ${value}`);
      const placeholder = `{${key}}`;
      url = url.replace(placeholder, value);
    });

    const queryString = queryParams
      .filter(({ key, value }) => key && value)
      .map(({ key, value }) => `${key}=${value}`)
      .join("&");

    if (queryString) {
      url += url.includes("?") ? `&${queryString}` : `?${queryString}`;
    }

    return url;
  };

  const handleResponse = (response, config) => {
    if (!response || !config || !Array.isArray(config)) {
      console.error("La réponse ou la configuration est invalide !");
      return;
    }

    const customFields = {};

    config.forEach(({ key, value }) => {
      try {
        const normalizedValue = value.startsWith(".") ? value : `.${value}`;
        const getter = new Function(
          "response",
          `return response${normalizedValue}`,
        );
        const fieldValue = getter(response);

        customFields[key] =
          fieldValue !== undefined ? fieldValue : "Non disponible";
      } catch (error) {
        console.error(
          `Erreur lors de l'accès à la clé "${key}" avec le chemin "${value}":`,
          error,
        );
        customFields[key] = "Erreur";
      }
    });

    const displayText = Object.entries(customFields)
      .map(([key, value]) => `${key}: ${value}`)
      .join("\n");

    console.log("Texte généré :", displayText);
    return displayText;
  };

  const handleChangeExampleContact = async () => {
    console.log("update example contact");
    setExampleContactError(false);
    setExampleContactLoading(true);
    try {
      const newExampleContact = await api.contact.maybeFindFirst({
        filter: {
          customerId: { equals: user.customer.id },
          userPhoneNumber: { equals: cleanPhone(exampleContactPhone) },
        },
        select: {
          id: true,
          firstName: true,
          lastName: true,
          hasOptIn: true,
          userPhoneNumber: true,
          updatedAt: true,
          customProperties: true,
        },
      });
      console.log(newExampleContact);
      if (newExampleContact) {
        setLastContact(newExampleContact);
      } else {
        setExampleContactError(true);
      }
      setExampleContactLoading(false);
    } catch (e) {
      setExampleContactError(true);
      setExampleContactLoading(false);
      console.log("error");
      console.log(e);
    }
    console.log(lastContact);
  };

  return (
    <>
      <SidePanel
        name="sidepanel-variables"
        isSidePanelOpen={isSidePanelVariableOpen}
        onClose={() => setIsSidePanelVariableOpen(false)}
        title="Variables"
      >
        <>
          {nativeVariables.sort().map((nativeVariable) => (
            <div
              key={nativeVariable}
              className="flex justify-center items-center w-full mb-2 text-center overflow-y-auto"
            >
              <Badge className="w-1/2 text-center min-h-8 mr-2">
                <div className="overflow-hidden break-words text-center w-full">
                  {variableCopied === nativeVariable
                    ? "Variable copied !"
                    : nativeVariable}
                </div>
              </Badge>
              <Button
                color="outline"
                className="h-6 hover:bg-gray-300 mr-2"
                onClick={() => {
                  handleVariableCopy(nativeVariable);
                }}
              >
                <Square2StackIcon className="w-5 h-5" />
              </Button>
              <Button
                color="outline"
                className="h-6 hover:bg-gray-300"
                onClick={() => {
                  handleVariableInsert(
                    nativeVariable,
                    selectedNode.data.method === "POST",
                  );
                }}
              >
                <ArrowDownOnSquareIcon className="w-5 h-5" />
              </Button>
            </div>
          ))}
          <br />
          {customVariables &&
            customVariables.sort().map((customVariable) => (
              <div
                key={customVariable}
                className="flex justify-center items-center w-full mb-2 text-center overflow-y-auto"
              >
                <Badge className="w-1/2 text-center min-h-8 mr-2">
                  <div className="overflow-hidden break-words text-center w-full">
                    {variableCopied === customVariable
                      ? "Variable copied !"
                      : customVariable}
                  </div>
                </Badge>
                <Button
                  color="outline"
                  className="h-6 hover:bg-gray-300 mr-2"
                  onClick={() => {
                    handleVariableCopy(customVariable);
                  }}
                >
                  <Square2StackIcon className="w-5 h-5" />
                </Button>
                <Button
                  color="outline"
                  className="h-6 hover:bg-gray-300"
                  onClick={() => {
                    handleVariableInsert(
                      customVariable,
                      selectedNode.data.method === "POST",
                    );
                  }}
                >
                  <ArrowDownOnSquareIcon className="w-5 h-5" />
                </Button>
              </div>
            ))}
        </>
      </SidePanel>

      <SidePanel
        name="sidepanel-webhook-test"
        isSidePanelOpen={isSidePanelTestOpen}
        onClose={() => {
          setIsSidePanelTestOpen(false);
          setResponseTestRequest(null);
        }}
        title="Preview"
        className="flex flex-col justify-between h-full"
      >
        <>
          <div className="flex-grow">
            <Field>
              <Text className="text-sm font-bold">Example contact</Text>
              {lastContact && (
                <>
                  <div className="mb-2">
                    <Text className="text-xs">{`First Name: ${lastContact.firstName}`}</Text>
                    <Text className="text-xs">{`Last Name: ${lastContact.lastName}`}</Text>
                    <Text className="text-xs">{`Optin: ${lastContact.hasOptIn}`}</Text>
                    {lastContact.customProperties && (
                      <>
                        {Object.keys(lastContact.customProperties).map(
                          (key) => (
                            <Text className="text-xs" key={key}>
                              {`${key}: ${lastContact.customProperties[key]}`}
                            </Text>
                          ),
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex">
                    <Input
                      type="text"
                      name="webhook-example-phone"
                      value={exampleContactPhone}
                      maxLength={11}
                      onChange={(e) => setExampleContactPhone(e.target.value)}
                      autoComplete="off"
                    />
                    <Button
                      onClick={() => handleChangeExampleContact()}
                      color="outline"
                      outline
                      className="w-96 border ml-2 shadow border-zinc-950/10"
                      loading={exampleContactLoading}
                    >
                      Change contact
                    </Button>
                  </div>
                  {exampleContactError && (
                    <Text className="text-red-600 text-xs">
                      No contact found with this phone number
                    </Text>
                  )}
                  {selectedNode.data.method === "POST" && (
                    <>
                      <Text className="text-sm font-bold mt-3">
                        Body with last updated example contact
                      </Text>
                      <div
                        className={`overflow-y-auto max-h-[250px] min-h-[20px] thin-scrollbar mt-1 mb-2 border rounded-customBorderRadius p-2 ${
                          jsonError ? "border-red-600" : "selection:"
                        }`}
                      >
                        <Text
                          className="text-xs  overflow-hidden"
                          style={{
                            whiteSpace: "pre-wrap",
                            overflowWrap: "break-word",
                            wordWrap: "break-word",
                          }}
                        >
                          {(() => {
                            const body = formatExampleBody(
                              selectedNode.data.body,
                            );
                            try {
                              return JSON.stringify(JSON.parse(body), null, 2);
                            } catch {
                              return body;
                            }
                          })()}
                        </Text>
                      </div>
                    </>
                  )}
                  {selectedNode.data.method === "GET" && (
                    <>
                      <Text className="text-sm font-bold mt-2">Url</Text>
                      <Text className="text-xs mt-1 mb-2">
                        {formatExampleBody(
                          buildUrl(
                            selectedNode.data.destinationUrl,
                            selectedNode.data.pathParams,
                            selectedNode.data.queryParams,
                          ),
                        )}
                      </Text>
                    </>
                  )}
                </>
              )}
            </Field>
            <div className="mt-3">
              {responseTestRequest &&
                responseTestRequest.statusCode &&
                selectedNode.data.method && (
                  <>
                    <div className="flex ">
                      <Text className="text-sm font-bold">Api response:</Text>
                      {responseTestRequest.statusCode
                        .toString()
                        .startsWith("20") ? (
                        <CheckCircleIcon className="text-green-500 h-6 w-6 mr-2" />
                      ) : (
                        <XCircleIcon className="text-red-600 h-6 w-6 mr-2" />
                      )}
                    </div>
                    <div className="w-full mt-1 mb-2 p-2 border rounded-customBorderRadius overflow-y-auto max-h-[250px] min-h-[20px] thin-scrollbar">
                      {console.log(responseTestRequest)}
                      <>
                        <Text
                          className="text-xs  overflow-hidden"
                          style={{
                            whiteSpace: "pre-wrap",
                            overflowWrap: "break-word",
                            wordWrap: "break-word",
                          }}
                        >
                          {typeof responseTestRequest.data === "object"
                            ? JSON.stringify(responseTestRequest, null, 2)
                            : responseTestRequest.data}
                        </Text>
                      </>
                    </div>
                  </>
                )}
              {responseTestRequest &&
                selectedNode?.data?.configResponse.length >= 1 &&
                selectedNode?.data?.configResponse[0]?.key !== "" && (
                  <>
                    <div>
                      <Text className="text-sm font-bold">
                        Update contact fields:
                      </Text>
                      <div className="w-full p-2 border rounded-customBorderRadius overflow-y-auto max-h-[170px] min-h-[20px] mb-2 thin-scrollbar">
                        {console.log(responseTestRequest)}
                        <>
                          {selectedNode.data.configResponse[0].key !== "" && (
                            <Text
                              className="text-xs "
                              style={{
                                whiteSpace: "pre-wrap",
                                overflowWrap: "break-word",
                                wordWrap: "break-word",
                              }}
                            >
                              {handleResponse(
                                responseTestRequest,
                                selectedNode?.data?.configResponse,
                              )}
                            </Text>
                          )}
                        </>
                      </div>
                    </div>
                  </>
                )}
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              className="px-3"
              loading={testRequestLoading}
              onClick={() => {
                const response = handleSendTestRequest();
                setResponseTestRequest(response);
                console.log(response);
              }}
              disabled={jsonError}
              color="secondaryButton"
            >
              Send Test Request
            </Button>
          </div>
        </>
      </SidePanel>

      <div className="flex justify-between items-center">
        <Heading className="text-lg">Webhook</Heading>
      </div>
      <Text className="mb-1 font-bold">Webhook name</Text>
      <Input
        type="text"
        name="webhook-name"
        value={selectedNode.data.name}
        maxLength={100}
        onChange={(e) =>
          handleMessageChange("name", e.target.value, nodes, setNodes)
        }
        autoComplete="off"
      />
      <Field className="mt-2">
        <Text className="mb-1 font-bold">Method</Text>
        <SelectInput
          placeholder="Select a method"
          defaultValue={methodOptions[0].value}
          value={selectedNode.data.method}
          onChange={(value) =>
            handleMessageChange("method", value, nodes, setNodes)
          }
          options={methodOptions}
          selected={selectedNode.data.method}
        />
      </Field>
      <Field className="mt-2">
        <Text className="mb-1 font-bold">Destination URL</Text>
        <Input
          type="url"
          name="webhook-url"
          value={selectedNode.data.destinationUrl}
          onChange={(e) => {
            let value = e.target.value;
            handleMessageChange("destinationUrl", value, nodes, setNodes);
          }}
          autoComplete="off"
        />
      </Field>
      <Divider className="mt-4 mb-3" />
      <DynamicList
        title="Headers"
        items={headers}
        setItems={setHeaders}
        stateKey="headers"
        addButtonLabel="Add header"
        nodes={nodes}
        setNodes={setNodes}
      />
      {selectedNode.data.method === "POST" && (
        <>
          <Divider className="mt-4 mb-3" />
          <Field className="mt-2">
            <div className="flex justify-between">
              <Text className="mb-1 font-bold">JSON Body</Text>
              <Link
                className="text-gray text-sm hover:underline text-gray-400 cursor-pointer"
                onClick={() => {
                  setIsSidePanelVariableOpen(true);
                  if (isSidePanelTestOpen) {
                    setIsSidePanelTestOpen(false);
                    setResponseTestRequest(null);
                  }
                }}
              >
                Add variables
              </Link>
            </div>

            <Textarea
              name="json-body"
              ref={textareaJsonBodyRef}
              invalid={jsonError}
              value={(() => {
                const body = selectedNode.data.body;

                if (!body || body === "{}" || body.includes("\n")) {
                  () => {
                    setJsonError(false);
                  };
                  return body || "{}";
                }

                try {
                  return JSON.stringify(JSON.parse(body), null, 2);
                } catch {
                  return body;
                }
              })()}
              wrap="hard"
              onChange={(e) => {
                handleMessageChange("body", e.target.value, nodes, setNodes);
                validateJson(e.target.value);
              }}
              autoComplete="off"
              style={{
                whiteSpace: "pre-wrap",
                overflowWrap: "break-word",
                fontFamily: "monospace",
              }}
            />
            {jsonError && (
              <>
                <ErrorMessage>Body must be formatted as a Json.</ErrorMessage>
                <ErrorMessage>Wrong format can not be saved</ErrorMessage>
              </>
            )}
          </Field>
        </>
      )}

      <>
        <Divider className="mt-4 mb-3" />
        <div className="relative">
          <Link
            className="text-gray text-sm hover:underline text-gray-400 cursor-pointer absolute right-0 top-0"
            onClick={() => {
              setIsSidePanelVariableOpen(true);
              if (isSidePanelTestOpen) {
                setIsSidePanelTestOpen(false);
                setResponseTestRequest(null);
              }
            }}
          >
            Add variables
          </Link>
          <DynamicList
            title="Path parameters"
            items={pathParams}
            setItems={setPathParams}
            stateKey="pathParams"
            addButtonLabel="Add path parameter"
            nodes={nodes}
            setNodes={setNodes}
          />
        </div>
        <DynamicList
          title="Query parameters"
          items={queryParams}
          setItems={setQueryParams}
          stateKey="queryParams"
          addButtonLabel="Add query parameter"
          nodes={nodes}
          setNodes={setNodes}
        />
      </>

      {selectedNode.data.method !== "" && (
        <>
          <Divider className="mt-4 mb-3" />
          <DynamicList
            title="Save response in custom fields"
            items={configResponse}
            setItems={setConfigResponse}
            stateKey="configResponse"
            addButtonLabel="Add a field"
            nodes={nodes}
            setNodes={setNodes}
            keyOptions={fieldsOptions}
            useSelectForKey={true}
          />
        </>
      )}
      <div className="flex justify-end items-center w-full mb-20">
        <Button
          className="my-3 px-3"
          color="optionButton"
          disabled={isSidePanelTestOpen}
          onClick={() => {
            handleOpenSidePanelTest();
          }}
        >
          Preview
          <ArrowsPointingOutIcon />
        </Button>
      </div>
    </>
  );
};

export default WebhookNodePanel;
